<template>
  <div>
    <!-- ไม่มีรอบรายงานข้อมูลใดๆ เลย (API พลาด/ยังไม่ตั้งรอบ) ก็ไม่ต้องแสดงกล่องเปล่า -->
    <v-card v-if="rounds.length > 0" class="alert-report-round hrvec-alert-card mb-3">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-calendar-check</v-icon>
        <span>กำหนดการรายงานข้อมูลบุคลากร</span>
        <v-spacer></v-spacer>
        <v-chip
          v-if="activeRound"
          color="green"
          dark
          small
          class="ml-2"
        >
          <v-icon left small>mdi-circle</v-icon>
          กำลังเปิดรับรายงาน
        </v-chip>
      </v-card-title>

      <v-card-text>
        <!-- แจ้งเตือนรอบที่เปิดอยู่ -->
        <v-alert
          v-if="activeRound"
          type="success"
          border="left"
          colored-border
          elevation="2"
          prominent
          class="mb-4"
        >
          <div class="d-flex align-center justify-space-between flex-wrap">
            <div>
              <div class="text-subtitle-1 font-weight-bold">
                <v-icon left color="green darken-2">mdi-folder-open</v-icon>
                {{ activeRound.round_name }} — ปีงบประมาณ {{ activeRound.cycle_fiscal_year }}
              </div>
              <div class="mt-1 ml-8">
                <v-icon small color="grey" class="mr-1">mdi-calendar-range</v-icon>
                ช่วงรับรายงาน:
                <strong>{{ activeRound.round_start_date | dateTH }}</strong>
                ถึง
                <strong>{{ activeRound.round_end_date | dateTH }}</strong>
              </div>
              <div class="ml-8">
                <v-icon small color="grey" class="mr-1">mdi-database</v-icon>
                ช่วงข้อมูล:
                {{ activeRound.round_data_period_start | dateTH }}
                ถึง
                {{ activeRound.round_data_period_end | dateTH }}
              </div>
              <div class="mt-1 ml-8" v-if="daysRemaining !== null">
                <v-icon small :color="daysRemaining <= 7 ? 'red' : 'orange'" class="mr-1">
                  mdi-clock-alert-outline
                </v-icon>
                <span :class="daysRemaining <= 7 ? 'red--text font-weight-bold' : 'orange--text'">
                  เหลืออีก {{ daysRemaining }} วัน ก่อนปิดรับรายงาน
                </span>
              </div>
            </div>
            <v-btn
              color="success"
              class="mt-2"
              to="/college/personnel_etc"
            >
              <v-icon left>mdi-send</v-icon>
              ส่งรายงาน
            </v-btn>
          </div>
        </v-alert>

        <!-- แจ้งเตือนใกล้ถึงกำหนดเปิดรอบ (ภายใน 15 วัน) -->
        <v-alert
          v-if="upcomingRound && !activeRound"
          type="info"
          border="left"
          colored-border
          elevation="2"
          class="mb-4"
        >
          <div class="text-subtitle-1 font-weight-bold">
            <v-icon left color="blue">mdi-calendar-clock</v-icon>
            รอบถัดไป: {{ upcomingRound.round_name }}
          </div>
          <div class="mt-1 ml-8">
            จะเปิดรับรายงานวันที่
            <strong>{{ upcomingRound.round_start_date | dateTH }}</strong>
            ถึง
            <strong>{{ upcomingRound.round_end_date | dateTH }}</strong>
          </div>
          <div class="ml-8">
            <v-icon small color="blue" class="mr-1">mdi-timer-sand</v-icon>
            อีก {{ daysUntilUpcoming }} วัน จะเปิดระบบ
          </div>
        </v-alert>

        <!-- ไม่มีรอบเปิดและไม่มีรอบใกล้เปิด -->
        <v-alert
          v-if="!activeRound && !upcomingRound && rounds.length > 0"
          type="warning"
          border="left"
          colored-border
          elevation="2"
          class="mb-4"
        >
          <v-icon left color="orange">mdi-information</v-icon>
          ขณะนี้ยังไม่มีรอบรายงานที่เปิดรับ กรุณารอ Admin เปิดรอบ
        </v-alert>

        <!-- ตาราง Timeline ปฏิทินทุกรอบ -->
        <div class="mt-4" v-if="rounds.length > 0">
          <div class="text-subtitle-2 font-weight-bold mb-2">
            <v-icon small class="mr-1">mdi-timeline-clock</v-icon>
            ปฏิทินรอบรายงานทั้งหมด — ปีงบประมาณ {{ fiscalYear }}
          </div>

          <v-simple-table dense>
            <thead>
              <tr>
                <th class="text-center">รอบ</th>
                <th class="text-center">ไตรมาส</th>
                <th class="text-center">ช่วงรับรายงาน</th>
                <th class="text-center">ช่วงข้อมูล</th>
                <th class="text-center">สถานะ</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="round in rounds"
                :key="round.round_id"
                :class="{ 'green lighten-5': round.round_status === 'open' }"
              >
                <td class="text-center">{{ round.round_name }}</td>
                <td class="text-center">
                  <v-chip x-small :color="quarterColor(round.round_quarter)" dark>
                    {{ round.round_quarter }}
                  </v-chip>
                </td>
                <td class="text-center">
                  {{ round.round_start_date | dateTH }} — {{ round.round_end_date | dateTH }}
                </td>
                <td class="text-center">
                  {{ round.round_data_period_start | dateTH }} — {{ round.round_data_period_end | dateTH }}
                </td>
                <td class="text-center">
                  <v-chip
                    x-small
                    :color="statusColor(round.round_status)"
                    dark
                  >
                    <v-icon left x-small>{{ statusIcon(round.round_status) }}</v-icon>
                    {{ statusLabel(round.round_status) }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "AlertReportRound",
  data() {
    return {
      ApiKey: "HRvec2021",
      rounds: [],
      activeRound: null,
      userSession: JSON.parse(sessionStorage.getItem("user")) || {},
      fiscalYear: new Date().getFullYear() + 543 + (new Date().getMonth() >= 9 ? 1 : 0)
    };
  },
  computed: {
    // จำนวนวันที่เหลือก่อนปิดรอบที่เปิดอยู่
    daysRemaining() {
      if (!this.activeRound) return null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endDate = new Date(this.activeRound.round_end_date);
      endDate.setHours(0, 0, 0, 0);
      const diff = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
      return diff >= 0 ? diff : 0;
    },

    // รอบถัดไปที่จะเปิด (draft ที่ start_date ใกล้ที่สุดภายใน 15 วัน)
    upcomingRound() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const fifteenDays = 15 * 24 * 60 * 60 * 1000;

      const upcoming = this.rounds
        .filter(r => {
          if (r.round_status !== "draft") return false;
          const startDate = new Date(r.round_start_date);
          startDate.setHours(0, 0, 0, 0);
          const diff = startDate - today;
          return diff > 0 && diff <= fifteenDays;
        })
        .sort((a, b) => new Date(a.round_start_date) - new Date(b.round_start_date));

      return upcoming.length > 0 ? upcoming[0] : null;
    },

    // จำนวนวันก่อนเปิดรอบถัดไป
    daysUntilUpcoming() {
      if (!this.upcomingRound) return null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = new Date(this.upcomingRound.round_start_date);
      startDate.setHours(0, 0, 0, 0);
      return Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
    }
  },
  async mounted() {
    await this.loadRounds();
    await this.loadActiveRound();
  },
  methods: {
    // ดึงรอบรายงานทั้งหมดของปีงบประมาณ
    async loadRounds() {
      try {
        let result = await this.$http.post("personnel_report_round.php", {
          ApiKey: this.ApiKey,
          action: "list_rounds",
          fiscal_year: this.fiscalYear
        });
        this.rounds = Array.isArray(result.data) ? result.data : [];
      } catch (e) {
        console.error("โหลดรอบรายงานผิดพลาด:", e);
      }
    },

    // ดึงรอบที่เปิดอยู่
    async loadActiveRound() {
      try {
        let result = await this.$http.post("personnel_report_round.php", {
          ApiKey: this.ApiKey,
          action: "get_active_round"
        });
        if (result.data && !result.data.status === false) {
          this.activeRound = result.data;
        } else if (result.data && result.data.round_id) {
          this.activeRound = result.data;
        }
      } catch (e) {
        console.error("โหลดรอบที่เปิดอยู่ผิดพลาด:", e);
      }
    },

    statusColor(status) {
      const map = {
        draft: "grey",
        open: "green",
        closing: "orange",
        closed: "red darken-1"
      };
      return map[status] || "grey";
    },

    statusIcon(status) {
      const map = {
        draft: "mdi-pencil-outline",
        open: "mdi-lock-open-variant",
        closing: "mdi-timer-sand",
        closed: "mdi-lock"
      };
      return map[status] || "mdi-help-circle";
    },

    statusLabel(status) {
      const map = {
        draft: "ร่าง",
        open: "เปิดรับ",
        closing: "กำลังปิด",
        closed: "ปิดแล้ว"
      };
      return map[status] || status;
    },

    quarterColor(quarter) {
      const map = {
        Q1: "indigo",
        Q2: "teal",
        Q3: "deep-orange",
        Q4: "purple"
      };
      return map[quarter] || "grey";
    }
  },
  filters: {
    dateTH(val) {
      if (!val) return "";
      const d = new Date(val);
      const months = [
        "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
        "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."
      ];
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear() + 543}`;
    }
  }
};
</script>

<style scoped>
.alert-report-round {
  margin: 16px 0;
  border-left: 4px solid #1976d2 !important;
}
</style>